import { SearchInput, UserList, Text, View } from '@/components'
import { ModalPage, ModalPageProps } from '../modals/ModalPage'
import { APIClient } from '@/services'
import { getFlatlistProps } from '@/utils'
import { alertMembersLimitExceeded, ChatListUsersFilters, ChatUser } from '@packages/chat'
import { useState } from 'react'
import { AppForms, Chat, theme } from '@/config'

export type AddMembersModalPageProps = Partial<ModalPageProps> & {
  onSave?: (selectedUsers: ChatUser[]) => void
  filter?: ChatListUsersFilters
  maxSelectedUsers?: number
}

export const AddMembersModalPage = ({
  onNextPage,
  onSave,
  buttonProps,
  visible,
  filter,
  maxSelectedUsers = Chat.Constants.maxSelectedMembers,
  ...rest
}: AddMembersModalPageProps) => {
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const selectedMembers = AppForms.chatGroup.use(s => s.values.members || []) as ChatUser[]

  const query = APIClient.Profiles.useProfiles({
    filter: {
      ...filter,
      name: search,
    },
    queryOptions: {
      enabled: !!visible,
      gcTime: 0,
      staleTime: 0,
    },
  })

  const flatListProps = getFlatlistProps(query, {
    noMoreItemsText: '',
  })

  function handleSelectUser(user: ChatUser) {
    const currentMembers = AppForms.chatGroup.state.get()?.members as ChatUser[]

    if (currentMembers.some(u => u.id === user.id)) {
      AppForms.chatGroup.setValues({
        members: currentMembers.filter(u => u.id !== user.id),
      })
    } else {
      if (currentMembers.length >= maxSelectedUsers) {
        alertMembersLimitExceeded(maxSelectedUsers)
        return
      }
      AppForms.chatGroup.setValues({
        members: [...currentMembers, user],
      })
    }
  }

  return (
    <ModalPage
      title='Add members'
      canGoBack
      buttonProps={{
        text: 'Next',
        disabled: loading || selectedMembers.length === 0 || selectedMembers.length > maxSelectedUsers,
        ...buttonProps,
        onPress: () => {
          if (loading) return
          setLoading(true)
          onSave ? onSave(selectedMembers) : onNextPage?.()
        }
      }}
      {...rest}
    >
      <UserList
        {...flatListProps}
        ListHeaderComponent={() => (
          <View style={['column', 'fullWidth', 'px:3', {
            breakpoints: {
              mobile: ['px:2'],
            },
          }]}>
            <SearchInput
              placeholder='Search'
              value={search}
              debounce={300}
              onSearchChange={setSearch}
              style={['noError']}
            />

            <Text text={`${selectedMembers.length}/${maxSelectedUsers}`} style={['c1', 'mt:2', { color: theme.colors.textParagraph }]} />
          </View>
        )}
        data={flatListProps.data as ChatUser[]}
        onPress={handleSelectUser}
        onSelect={handleSelectUser}
        selectable
        usersSelected={selectedMembers.map(user => user.id)}
        letterSeparatorShown
        style={{
          wrapper: {
            maxHeight: '60vh',
            overflow: 'auto',
            paddingBottom: theme.spacing.value(2),
            [theme.media.down('tabletSmall')]: {
              maxHeight: 'calc(100vh - 180px)',
              height: 'calc(100vh - 180px)',
            },
          },
        }}
      />
    </ModalPage>
  )
}
